import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, ChevronRight, ExternalLink } from 'lucide-react';

const InstagramIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const contactItems = [
  {
    icon: MapPin,
    title: "Adres",
    subtitle: "Bizi Ziyaret Edin",
    lines: [
      "Bahçelievler Mahallesi, Talat Paşa Cd No:17",
      "34180 Bahçelievler / İstanbul"
    ],
    gradient: "from-[#e65100] to-[#ff9800]",
    glow: "rgba(230,81,0,0.18)",
    iconBg: "from-[#fff7ed] to-[#ffedd5]",
    iconColor: "#e65100",
    accentColor: "#e65100",
  },
  {
    icon: Phone,
    title: "Telefon",
    subtitle: "Bizi Arayın",
    lines: ["+90 (533) 364 40 99"],
    gradient: "from-[#e65100] to-[#fb8c00]",
    glow: "rgba(230,81,0,0.15)",
    iconBg: "from-[#fff7ed] to-[#ffedd5]",
    iconColor: "#e65100",
    accentColor: "#e65100",
  },
  {
    icon: Mail,
    title: "E-posta",
    subtitle: "Mesaj Gönderin",
    lines: ["flyingslapsk@gmail.com"],
    gradient: "from-[#e65100] to-[#f97316]",
    glow: "rgba(230,81,0,0.15)",
    iconBg: "from-[#fff7ed] to-[#ffedd5]",
    iconColor: "#e65100",
    accentColor: "#e65100",
  },
];

const Contact: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.title = "Flying Slap - İletişim";
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const leftColumn = (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 }}
      className="flex flex-col gap-5 w-full"
    >
      {contactItems.map((item, i) => {
        const isPhone = item.title === "Telefon";
        const isCopyable = item.title === "Adres";
        const isLink = !isCopyable;
        const linkUrl = isLink ? (item.title === "E-posta" ? `mailto:${item.lines[0]}` : `tel:${item.lines[0].replace(/[\s()]/g, '')}`) : "";

        const CardContent = (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.12 }}
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            onClick={isCopyable ? () => {
              navigator.clipboard.writeText(item.lines.join(', '));
              alert('Adres kopyalandı!');
            } : undefined}
            style={{
              background: '#ffffff',
              borderRadius: '20px',
              border: '1px solid #f1f5f9',
              boxShadow: `0 4px 24px ${item.glow}, 0 1px 4px rgba(0,0,0,0.04)`,
              padding: isMobile ? '16px' : '20px 24px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: isMobile ? '12px' : '16px',
              cursor: 'pointer',
              position: 'relative',
              overflow: 'hidden',
              transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
              width: '100%',
              textAlign: 'left',
              pointerEvents: 'auto',
            }}
            className="group"
          >
            <div style={{
              display: 'flex', gap: isMobile ? '12px' : '16px', width: '100%',
              filter: 'none',
              opacity: 1,
            }}>
              <div style={{
                width: isMobile ? '38px' : '48px', height: isMobile ? '38px' : '48px', borderRadius: isMobile ? '10px' : '14px', flexShrink: 0,
                background: `linear-gradient(135deg, ${item.iconBg.replace('from-[', '').replace('] to-[', ',').replace(']', '')})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 14px ${item.glow}`,
              }}>
                <item.icon size={isMobile ? 18 : 20} color={item.iconColor} strokeWidth={2} />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: isMobile ? '11px' : '12px', fontWeight: 800, textTransform: 'uppercase',
                  letterSpacing: '0.2em', color: item.accentColor, marginBottom: '2px'
                }}>{item.title}</p>
                <p style={{ fontSize: isMobile ? '10px' : '11px', color: '#94a3b8', fontWeight: 500, marginBottom: '6px' }}>{item.subtitle}</p>
                {item.lines.map((line, j) => (
                  <p key={j} style={{
                    color: '#0f2d5e', fontSize: isMobile ? '13px' : '15px', fontWeight: 700,
                    lineHeight: '1.5', letterSpacing: '-0.01em'
                  }}>{line}</p>
                ))}
              </div>
            </div>

            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
              background: `linear-gradient(to right, ${item.iconColor}, transparent)`,
              borderRadius: '20px 20px 0 0',
              opacity: 1
            }} />

            <div style={{
              position: 'absolute', bottom: '-20px', right: '-20px',
              width: '80px', height: '80px', borderRadius: '50%',
              background: `radial-gradient(circle, ${item.glow} 0%, transparent 70%)`,
              pointerEvents: 'none',
            }} />
          </motion.div>
        );

        return isLink ? (
          <a href={linkUrl} key={i} style={{ textDecoration: 'none', display: 'block' }}>
            {CardContent}
          </a>
        ) : (
          <React.Fragment key={i}>
            {CardContent}
          </React.Fragment>
        );
      })}


    </motion.div>
  );

  const rightColumn = (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.2 }}
      className="flex flex-col gap-5 w-full"
    >
      <div style={{
        borderRadius: '20px', overflow: 'hidden',
        border: '2px solid #e0f2fe',
        boxShadow: '0 8px 32px rgba(14,165,233,0.1)',
      }}>
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3011.16068833839!2d28.8665269!3d40.999856!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cabb4e8edc0cff%3A0x7884bdb3197d4745!2sElit%20Gencler%20Koleji!5e0!3m2!1str!2str!4v1714300000000!5m2!1str!2str"
          width="100%"
          height={isMobile ? "200" : "280"}
          style={{ border: 0, display: 'block' }}
          allowFullScreen={false}
          loading="lazy"
          title="Konum"
        />
      </div>

      <motion.a
        href="https://instagram.com/flyingslap"
        target="_blank"
        rel="noreferrer"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        whileHover={{ y: -4 }}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: isMobile ? '12px' : '20px',
          borderRadius: '20px',
          background: '#ffffff',
          border: '1px solid #f1f5f9',
          boxShadow: '0 4px 24px rgba(217,70,239,0.08)',
          padding: isMobile ? '16px' : '20px',
          cursor: 'pointer',
          textDecoration: 'none',
          transition: 'box-shadow 0.3s ease, border-color 0.3s ease',
          position: 'relative', overflow: 'hidden',
        }}
        className="group"
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(to right, #f59e0b, #ec4899, #8b5cf6)', borderRadius: '20px 20px 0 0' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px' }}>
          <div style={{
            width: isMobile ? '38px' : '48px', height: isMobile ? '38px' : '48px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b, #ec4899, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '2px', flexShrink: 0,
            boxShadow: '0 4px 14px rgba(236,72,153,0.3)',
          }}>
            <div style={{
              width: '100%', height: '100%', background: '#fff', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1e293b'
            }}>
              <InstagramIcon />
            </div>
          </div>

          <div>
            <p style={{ color: '#0f2d5e', fontWeight: 800, fontSize: isMobile ? '14px' : '15px', letterSpacing: '-0.02em', lineHeight: 1.2 }}>Instagram'da Takip Et</p>
            <p style={{ color: '#64748b', fontSize: isMobile ? '11px' : '12px', fontWeight: 500, marginTop: '2px' }}>Hızlı iletişim ve içerikler için</p>
            <p style={{ color: '#ec4899', fontWeight: 700, fontSize: isMobile ? '11px' : '12px', marginTop: '2px', letterSpacing: '0.02em' }}>@flyingslap</p>
          </div>
        </div>

        <div style={{
          width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', borderRadius: '50%', border: '1.5px solid #e2e8f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <ExternalLink size={isMobile ? 14 : 16} color="#94a3b8" />
        </div>
      </motion.a>
    </motion.div>
  );

  return (
    <div className="min-h-screen px-6 relative overflow-hidden font-body" style={{ background: '#ffffff', paddingTop: isMobile ? '100px' : '140px', paddingBottom: isMobile ? '60px' : '100px' }}>
      <div style={{
        position: 'absolute', top: '-120px', right: '-100px',
        width: '500px', height: '500px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(14,165,233,0.07) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0
      }} />
      <div style={{
        position: 'absolute', bottom: '-80px', left: '-80px',
        width: '400px', height: '400px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99,102,241,0.06) 0%, transparent 70%)',
        pointerEvents: 'none', zIndex: 0
      }} />

      <div className="max-w-7xl mx-auto w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: isMobile ? '40px' : '80px', textAlign: 'center' }}
        >
          <h1 className={`${isMobile ? 'text-4xl' : 'text-5xl md:text-7xl'} font-black font-headline uppercase tracking-tighter leading-[0.85]`}>
            <span style={{ color: '#e65100' }}>İLETİŞİM</span> <span style={{ color: '#0f2d5e' }}>BİLGİLERİ</span>
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {isMobile ? (
            <>
              {rightColumn}
              {leftColumn}
            </>
          ) : (
            <>
              {leftColumn}
              {rightColumn}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
